
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  deleteField,
  WriteBatch,
  QueryDocumentSnapshot,
  QuerySnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Registrant,
  HistoricalRecord,
  TripParticipant,
  EventOption,
  WatchlistItem,
  OutlookMeetingRecord,
  InventoryAccessCode,
  InventoryAccessCodeMap,
  InventoryItem,
  InventoryLog,
  InventoryRequestStatus,
  ReplenishmentRequest
} from "../types";
import { buildPublicRosterPayload } from "../utils/publicRoster";
import { buildCopiedInventoryItemData } from "../utils/inventoryCarryForward";

// Collection References
const REG_COLLECTION = "registrations";
const PUBLIC_ROSTER_COLLECTION = "public_roster"; // New Secure Collection
const HIST_COLLECTION = "historical_records";
const TRIP_PARTICIPANTS_COLLECTION = "trip_participants";
const EVENTS_COLLECTION = "events";
const OUTLOOK_RECORDS_COLLECTION = "outlook_records";
const INVENTORY_ITEMS_COLLECTION = "inventory_items";
const REPLENISHMENT_REQUESTS_COLLECTION = "replenishment_requests";
const INVENTORY_LOGS_COLLECTION = "inventory_logs";
const SETTINGS_COLLECTION = "settings";
const ACCESS_DOC_ID = "access_control";
const INVENTORY_ACCESS_DOC_ID = "inventory_access";
const INVENTORY_CODES_DOC_ID = "inventory_codes";
const GENERAL_SETTINGS_DOC_ID = "general";

const mapPublicRosterDoc = (snapshotDoc: { id: string; data: () => Record<string, unknown> }): Registrant => ({
  ...snapshotDoc.data(),
  id: snapshotDoc.id,
  isApproved: true,
} as Registrant);

// --- Registrations ---

/**
 * [ADMIN ONLY] Get all registrations including sensitive data.
 * Should only be called after admin auth verification.
 */
export const getAdminRegistrations = async (): Promise<Registrant[]> => {
  const q = query(collection(db, REG_COLLECTION), orderBy("submissionDate", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Registrant));
};

/**
 * [PUBLIC SAFE] Get approved registrations from the dedicated public roster.
 * This reads from a separate collection populated by Cloud Functions (Method A).
 * This collection DOES NOT contain PII (Phone, Email, etc.).
 */
export const getApprovedRegistrations = async (): Promise<Registrant[]> => {
  try {
    // Method A: Read from the specific projection collection
    const snapshot = await getDocs(collection(db, PUBLIC_ROSTER_COLLECTION));

    if (snapshot.empty) {
      console.log("Public roster is empty. Please ensure Cloud Functions are deployed and data is synced.");
      return [];
    }

    return snapshot.docs.map(mapPublicRosterDoc);
  } catch (error) {
    console.error("Error fetching public roster:", error);
    return [];
  }
};

/**
 * [SECURE QUERY via Cloud Function] Find registrations by contact info
 * Used for "My Registration" page to verify identity
 * This uses a Cloud Function to bypass security rules safely
 */
export const findRegistrationsByContact = async (
  contact: { email?: string; name?: string; phone: string }
): Promise<Registrant[]> => {
  const { functions } = await import("../firebase");
  const getMyRegistration = httpsCallable(functions, 'getMyRegistration');
  const result = await getMyRegistration(contact);
  return result.data as Registrant[];
};

/**
 * [SECURE UPDATE via Cloud Function] Cancel a specific event from registration
 * This uses a Cloud Function to safely update the registration
 */
export const cancelEventFromRegistration = async (
  registrationId: string,
  eventId: string,
  _currentEvents: string[],
  email: string,
  phone: string
): Promise<void> => {
  const { functions } = await import("../firebase");
  const cancelRegistration = httpsCallable(functions, 'cancelRegistration');
  await cancelRegistration({ registrationId, eventId, email, phone });
};

export const addRegistration = async (data: Omit<Registrant, 'id'>): Promise<Registrant> => {
  // ✅ 允許傳入的 isApproved 值（支援自動審核通過）
  // 如果沒有提供，預設為 false
  const dataWithApproval = removeUndefined({
    isApproved: false,  // 預設值
    ...data,            // 允許傳入的 isApproved 覆蓋預設值
  });

  const batch = writeBatch(db);

  // 1. 寫入私有集合
  const privateRef = doc(collection(db, REG_COLLECTION));
  batch.set(privateRef, dataWithApproval);

  // 2. 如果已審核通過，雙寫到公開名單（支援自動審核通過的人員立即顯示）
  if (dataWithApproval.isApproved === true) {
    const publicRef = doc(db, PUBLIC_ROSTER_COLLECTION, privateRef.id);
    const safeData = {
      ...buildPublicRosterPayload(privateRef.id, dataWithApproval),
      lastUpdated: serverTimestamp(),
    };
    batch.set(publicRef, removeUndefined(safeData));
  }

  await batch.commit();

  return { ...dataWithApproval, id: privateRef.id } as Registrant;
};

export const submitRegistration = async (
  data: Omit<Registrant, 'id' | 'submissionDate'>
): Promise<Registrant> => {
  const { functions } = await import("../firebase");
  const submitRegistrationCallable = httpsCallable(functions, 'submitRegistration');
  const result = await submitRegistrationCallable({ registration: data });
  return (result.data as { registration: Registrant }).registration;
};

// Helper to remove undefined values for Firestore
const removeUndefined = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((v: any) => removeUndefined(v));
  }
  // Handle objects
  const newObj: any = {};
  Object.keys(obj).forEach(key => {
    const value = removeUndefined(obj[key]);
    if (value !== undefined) {
      newObj[key] = value;
    }
  });
  return newObj;
};

export const updateRegistrant = async (registrant: Registrant): Promise<void> => {
  const { id, ...data } = registrant;
  const sanitizedData = removeUndefined(data);

  const batch = writeBatch(db);

  // 1. Always update the main private record
  const privateRef = doc(db, REG_COLLECTION, id);
  batch.update(privateRef, sanitizedData);

  // 2. Dual-Write Logic for Public Roster
  const publicRef = doc(db, PUBLIC_ROSTER_COLLECTION, id);

  if (registrant.isApproved === true) {
    // If approved, sync SAFE data to public roster
    const safeData = {
      ...buildPublicRosterPayload(id, registrant),
      lastUpdated: serverTimestamp(),
    };
    batch.set(publicRef, removeUndefined(safeData));
  } else {
    // If not approved (or hidden), remove from public roster
    batch.delete(publicRef);
  }

  await batch.commit();
};

export const updateEventAndRegistrants = async (
  event: EventOption,
  registrants: Registrant[]
): Promise<void> => {
  const batch = writeBatch(db);

  const { id: eventId, ...eventData } = event;
  batch.set(doc(db, EVENTS_COLLECTION, eventId), removeUndefined(eventData), { merge: true });

  registrants.forEach(registrant => {
    const { id, ...data } = registrant;
    const privateRef = doc(db, REG_COLLECTION, id);
    batch.update(privateRef, removeUndefined(data));

    const publicRef = doc(db, PUBLIC_ROSTER_COLLECTION, id);
    if (registrant.isApproved === true) {
      const safeData = {
        ...buildPublicRosterPayload(id, registrant),
        lastUpdated: serverTimestamp(),
      };
      batch.set(publicRef, removeUndefined(safeData));
    } else {
      batch.delete(publicRef);
    }
  });

  await batch.commit();
};

/**
 * Delete a registrant completely from the system
 * Removes from both private registrations collection and public roster
 * @param registrantId - The ID of the registrant to delete
 */
export const deleteRegistrant = async (registrantId: string): Promise<void> => {
  const batch = writeBatch(db);

  // 1. Delete from main private registrations collection
  const privateRef = doc(db, REG_COLLECTION, registrantId);
  batch.delete(privateRef);

  // 2. Delete from public roster (if exists)
  const publicRef = doc(db, PUBLIC_ROSTER_COLLECTION, registrantId);
  batch.delete(publicRef);

  await batch.commit();
};

// --- Real-time Sync (Snapshot Listeners) ---

/**
 * [ADMIN ONLY] Real-time listener for all registrations
 * Returns an unsubscribe function to stop listening when component unmounts
 *
 * @param onUpdate - Callback function called when data changes
 * @param onError - Callback function called when error occurs
 * @returns Unsubscribe function
 */
export const subscribeToRegistrations = (
  onUpdate: (registrations: Registrant[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  const q = query(collection(db, REG_COLLECTION), orderBy("submissionDate", "desc"));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Registrant));
      onUpdate(data);
    },
    (error) => {
      console.error("Registrations real-time sync error:", error);
      onError(error as Error);
    }
  );

  return unsubscribe;
};

/**
 * [PUBLIC SAFE] Real-time listener for public roster
 * Returns an unsubscribe function to stop listening when component unmounts
 *
 * @param onUpdate - Callback function called when data changes
 * @param onError - Callback function called when error occurs
 * @returns Unsubscribe function
 */
export const subscribeToPublicRoster = (
  onUpdate: (registrations: Registrant[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  const unsubscribe = onSnapshot(
    collection(db, PUBLIC_ROSTER_COLLECTION),
    (snapshot) => {
      const data = snapshot.docs.map(mapPublicRosterDoc);
      onUpdate(data);
    },
    (error) => {
      console.error("Public roster real-time sync error:", error);
      onError(error as Error);
    }
  );

  return unsubscribe;
};

// --- Historical Data ---

/**
 * 🚀 優化版：分頁加載歷史記錄
 * 減少初始加載時間和 Firestore 讀取成本（從 3000 reads → 500 reads）
 *
 * @param limitCount - 每頁數量（預設 500）
 * @param lastDocId - 上一頁最後一筆記錄的 ID（用於分頁）
 * @returns 包含資料和分頁信息的物件
 */
export const getHistoricalDataPaginated = async (
  limitCount: number = 500,
  lastDocId?: string
): Promise<{ data: HistoricalRecord[]; lastId: string | null; hasMore: boolean }> => {
  try {
    let q = query(
      collection(db, HIST_COLLECTION),
      orderBy('name'),
      limit(limitCount + 1) // +1 用於檢測是否有下一頁
    );

    if (lastDocId) {
      const lastDocRef = doc(db, HIST_COLLECTION, lastDocId);
      const lastDocSnap = await getDoc(lastDocRef);
      if (lastDocSnap.exists()) {
        q = query(
          collection(db, HIST_COLLECTION),
          orderBy('name'),
          startAfter(lastDocSnap),
          limit(limitCount + 1)
        );
      }
    }

    const snapshot = await getDocs(q);
    const docs = snapshot.docs;

    // 檢查是否有更多資料
    const hasMore = docs.length > limitCount;
    const dataToReturn = hasMore ? docs.slice(0, limitCount) : docs;

    return {
      // 全欄位帶出（含補充的 churches、contactHashes）
      data: dataToReturn.map(doc => ({ ...doc.data(), id: doc.id } as HistoricalRecord)),
      lastId: dataToReturn.length > 0 ? dataToReturn[dataToReturn.length - 1].id : null,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching paginated historical data:', error);
    return { data: [], lastId: null, hasMore: false };
  }
};

/**
 * 傳統版：一次加載所有歷史記錄（不建議用於大量資料）
 * 僅在資料量小於 500 筆時使用
 */
export const getHistoricalData = async (): Promise<HistoricalRecord[]> => {
  // 🚀 優化：使用分頁加載所有資料（避免一次性大量讀取）
  const allData: HistoricalRecord[] = [];
  let lastId: string | null = null;
  let hasMore = true;

  // 批次加載所有資料（每批 500 筆）
  while (hasMore) {
    const { data, lastId: newLastId, hasMore: more } = await getHistoricalDataPaginated(500, lastId || undefined);
    allData.push(...data);
    lastId = newLastId;
    hasMore = more;

    // 安全機制：最多加載 10,000 筆（20 批）
    if (allData.length >= 10000) {
      console.warn('⚠️ 歷史記錄超過 10,000 筆，已停止加載。請考慮使用分頁版本。');
      break;
    }
  }

  return allData;
};

/**
 * 讀取過往出隊名單（trip_participants，管理員專用）。
 * 分頁讀取（每批 500 筆，依 name 排序）；集合不存在或讀取失敗時回傳空陣列。
 */
export const getTripParticipants = async (): Promise<TripParticipant[]> => {
  const allData: TripParticipant[] = [];
  const pageSize = 500;

  try {
    let lastDoc: QueryDocumentSnapshot | null = null;
    while (true) {
      const snapshot: QuerySnapshot = await getDocs(lastDoc
        ? query(collection(db, TRIP_PARTICIPANTS_COLLECTION), orderBy('name'), startAfter(lastDoc), limit(pageSize))
        : query(collection(db, TRIP_PARTICIPANTS_COLLECTION), orderBy('name'), limit(pageSize)));

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        allData.push({
          id: docSnap.id,
          name: typeof data.name === 'string' ? data.name : '',
          years: Array.isArray(data.years) ? data.years.map(Number).filter(Number.isFinite) : [],
          tripCount: Number(data.tripCount) || 0,
          source: typeof data.source === 'string' ? data.source : '',
          ...(Array.isArray(data.churches)
            ? { churches: data.churches.filter((value: unknown): value is string => typeof value === 'string') }
            : {}),
          ...(Array.isArray(data.contactHashes)
            ? { contactHashes: data.contactHashes.filter((value: unknown): value is string => typeof value === 'string') }
            : {}),
        });
      });

      if (snapshot.docs.length < pageSize) break;
      lastDoc = snapshot.docs[snapshot.docs.length - 1];

      // 安全機制：最多加載 10,000 筆
      if (allData.length >= 10000) {
        console.warn('⚠️ 出隊名單超過 10,000 筆，已停止加載。');
        break;
      }
    }
  } catch (error) {
    console.error('Error fetching trip participants:', error);
    return [];
  }

  return allData;
};

export const saveHistoricalBatch = async (records: HistoricalRecord[]): Promise<void> => {
  const batch = writeBatch(db);
  records.forEach(record => {
    const docRef = doc(collection(db, HIST_COLLECTION));
    const { id, ...data } = record;
    batch.set(docRef, data);
  });
  await batch.commit();
};

// --- Events ---

export const getEvents = async (): Promise<EventOption[]> => {
  const snapshot = await getDocs(collection(db, EVENTS_COLLECTION));
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as EventOption));
};

export const addEvent = async (event: EventOption): Promise<void> => {
  const { id, ...data } = event;
  await setDoc(doc(db, EVENTS_COLLECTION, id), data);
};

export const updateEvent = async (event: EventOption): Promise<void> => {
  const { id, ...data } = event;
  // Use set with merge:true to handle both new and existing documents
  await setDoc(doc(db, EVENTS_COLLECTION, id), removeUndefined(data), { merge: true });
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  await deleteDoc(doc(db, EVENTS_COLLECTION, eventId));
};

// --- Inventory ---

const sortInventoryItems = (items: InventoryItem[]): InventoryItem[] =>
  [...items].sort((a, b) =>
    a.department.localeCompare(b.department, 'zh-Hant') ||
    a.name.localeCompare(b.name, 'zh-Hant') ||
    (a.expiryDate || '').localeCompare(b.expiryDate || '')
  );

const sortReplenishmentRequests = (requests: ReplenishmentRequest[]): ReplenishmentRequest[] =>
  [...requests].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));

const makeInventoryLog = (
  action: InventoryLog['action'],
  actor: string,
  payload: Omit<InventoryLog, 'id' | 'action' | 'createdBy' | 'createdAt'>
): Omit<InventoryLog, 'id'> => removeUndefined({
  ...payload,
  action,
  createdBy: actor,
  createdAt: new Date().toISOString(),
});

export const subscribeToInventoryItems = (
  onUpdate: (items: InventoryItem[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  const unsubscribe = onSnapshot(
    collection(db, INVENTORY_ITEMS_COLLECTION),
    (snapshot) => {
      const items = snapshot.docs
        .map(snapshotDoc => ({ ...snapshotDoc.data(), id: snapshotDoc.id } as InventoryItem))
        .filter(item => item.isDeleted !== true);
      onUpdate(sortInventoryItems(items));
    },
    (error) => {
      console.error("Inventory real-time sync error:", error);
      onError(error as Error);
    }
  );

  return unsubscribe;
};

export const saveInventoryItem = async (
  item: Omit<InventoryItem, 'id'> & { id?: string },
  actor: string,
  previousItem?: InventoryItem
): Promise<InventoryItem> => {
  const now = new Date().toISOString();
  const itemRef = item.id
    ? doc(db, INVENTORY_ITEMS_COLLECTION, item.id)
    : doc(collection(db, INVENTORY_ITEMS_COLLECTION));

  const nextItem: InventoryItem = removeUndefined({
    ...item,
    id: itemRef.id,
    isDeleted: false,
    createdAt: previousItem?.createdAt || item.createdAt || now,
    createdBy: previousItem?.createdBy || item.createdBy || actor,
    updatedAt: now,
    updatedBy: actor,
  });

  const { id, ...data } = nextItem;
  const batch = writeBatch(db);
  batch.set(itemRef, removeUndefined(data), { merge: true });
  batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
    previousItem ? 'update' : 'create',
    actor,
    {
      department: nextItem.department,
      itemId: id,
      itemName: nextItem.name,
      before: previousItem ? removeUndefined(previousItem) : undefined,
      after: removeUndefined(nextItem),
      eventId: nextItem.eventId,
      eventLabel: nextItem.eventLabel,
    }
  ));

  await batch.commit();
  return nextItem;
};

export const deleteInventoryItem = async (
  item: InventoryItem,
  actor: string
): Promise<void> => {
  const now = new Date().toISOString();
  const deletedItem = removeUndefined({
    ...item,
    isDeleted: true,
    updatedAt: now,
    updatedBy: actor,
  });

  const batch = writeBatch(db);
  const itemRef = doc(db, INVENTORY_ITEMS_COLLECTION, item.id);
  const { id: _id, ...data } = deletedItem;
  batch.set(itemRef, removeUndefined(data), { merge: true });
  batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
    'delete',
    actor,
    {
      department: item.department,
      itemId: item.id,
      itemName: item.name,
      before: removeUndefined(item),
      after: deletedItem,
      eventId: item.eventId,
      eventLabel: item.eventLabel,
    }
  ));

  await batch.commit();
};

export const copyInventoryItemsFromEvent = async (
  sourceItems: InventoryItem[],
  targetEventId: string,
  targetEventLabel: string,
  actor: string
): Promise<InventoryItem[]> => {
  const now = new Date().toISOString();
  const batch = writeBatch(db);
  const newItems: InventoryItem[] = [];

  sourceItems.forEach(source => {
    const itemRef = doc(collection(db, INVENTORY_ITEMS_COLLECTION));
    const nextItem: InventoryItem = removeUndefined({
      id: itemRef.id,
      ...buildCopiedInventoryItemData(source, targetEventId, targetEventLabel, actor, now),
    });

    const { id, ...data } = nextItem;
    batch.set(itemRef, data);
    batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
      'create',
      actor,
      {
        department: nextItem.department,
        itemId: id,
        itemName: nextItem.name,
        after: removeUndefined(nextItem),
        eventId: targetEventId,
        eventLabel: targetEventLabel,
      }
    ));
    newItems.push(nextItem);
  });

  await batch.commit();
  return sortInventoryItems(newItems);
};

export const subscribeToReplenishmentRequests = (
  onUpdate: (requests: ReplenishmentRequest[]) => void,
  onError: (error: Error) => void
): (() => void) => {
  const unsubscribe = onSnapshot(
    collection(db, REPLENISHMENT_REQUESTS_COLLECTION),
    (snapshot) => {
      const requests = snapshot.docs.map(snapshotDoc => ({
        ...snapshotDoc.data(),
        id: snapshotDoc.id
      } as ReplenishmentRequest));
      onUpdate(sortReplenishmentRequests(requests));
    },
    (error) => {
      console.error("Replenishment request sync error:", error);
      onError(error as Error);
    }
  );

  return unsubscribe;
};

export const createReplenishmentRequest = async (
  request: Omit<ReplenishmentRequest, 'id' | 'status' | 'requestedAt' | 'requestedBy'>,
  actor: string
): Promise<ReplenishmentRequest> => {
  const requestRef = doc(collection(db, REPLENISHMENT_REQUESTS_COLLECTION));
  const nextRequest: ReplenishmentRequest = removeUndefined({
    ...request,
    id: requestRef.id,
    status: 'submitted',
    requestedBy: actor,
    requestedAt: new Date().toISOString(),
  });

  const { id, ...data } = nextRequest;
  const firstItem = nextRequest.items[0];
  const batch = writeBatch(db);
  batch.set(requestRef, removeUndefined(data));
  batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
    'replenishment_request',
    actor,
    {
      department: nextRequest.department,
      itemId: firstItem?.inventoryItemId,
      itemName: firstItem?.name,
      requestId: id,
      after: removeUndefined(nextRequest),
      eventId: nextRequest.eventId,
      eventLabel: nextRequest.eventLabel,
    }
  ));

  await batch.commit();
  return nextRequest;
};

export const updateReplenishmentRequestStatus = async (
  request: ReplenishmentRequest,
  status: InventoryRequestStatus,
  actor: string
): Promise<void> => {
  const now = new Date().toISOString();
  const nextRequest: ReplenishmentRequest = removeUndefined({
    ...request,
    status,
    handledBy: status === 'handled' ? actor : request.handledBy,
    handledAt: status === 'handled' ? now : request.handledAt,
    cancelledBy: status === 'cancelled' ? actor : request.cancelledBy,
    cancelledAt: status === 'cancelled' ? now : request.cancelledAt,
  });

  const { id, ...data } = nextRequest;
  const batch = writeBatch(db);
  batch.set(doc(db, REPLENISHMENT_REQUESTS_COLLECTION, id), removeUndefined(data), { merge: true });
  batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
    'request_status',
    actor,
    {
      department: request.department,
      requestId: id,
      before: removeUndefined(request),
      after: removeUndefined(nextRequest),
      eventId: request.eventId,
      eventLabel: request.eventLabel,
    }
  ));

  await batch.commit();
};

export const updateReplenishmentRequestItems = async (
  request: ReplenishmentRequest,
  items: ReplenishmentRequest['items'],
  actor: string
): Promise<void> => {
  const now = new Date().toISOString();
  const previousItemsById = new Map(request.items.map(item => [item.id, item]));
  const nextRequest: ReplenishmentRequest = removeUndefined({
    ...request,
    items: items.map(item => {
      const previousItem = previousItemsById.get(item.id);
      const quantityChanged = previousItem?.requestedQuantity !== item.requestedQuantity;
      return removeUndefined({
        ...item,
        ...(quantityChanged ? {
          quantityEditedBy: actor,
          quantityEditedAt: now,
        } : {}),
      });
    }),
    quantityEditedBy: actor,
    quantityEditedAt: now,
  });

  const { id, ...data } = nextRequest;
  const batch = writeBatch(db);
  batch.set(doc(db, REPLENISHMENT_REQUESTS_COLLECTION, id), removeUndefined(data), { merge: true });
  batch.set(doc(collection(db, INVENTORY_LOGS_COLLECTION)), makeInventoryLog(
    'request_update',
    actor,
    {
      department: request.department,
      requestId: id,
      before: removeUndefined(request),
      after: removeUndefined(nextRequest),
      eventId: request.eventId,
      eventLabel: request.eventLabel,
    }
  ));

  await batch.commit();
};

export const subscribeToInventoryLogs = (
  onUpdate: (logs: InventoryLog[]) => void,
  onError: (error: Error) => void,
  logLimit: number = 120
): (() => void) => {
  const q = query(collection(db, INVENTORY_LOGS_COLLECTION), orderBy("createdAt", "desc"), limit(logLimit));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const logs = snapshot.docs.map(snapshotDoc => ({
        ...snapshotDoc.data(),
        id: snapshotDoc.id
      } as InventoryLog));
      onUpdate(logs);
    },
    (error) => {
      console.error("Inventory log sync error:", error);
      onError(error as Error);
    }
  );

  return unsubscribe;
};

// --- Outlook Meeting Records ---

export const getOutlookRecords = async (year?: number): Promise<OutlookMeetingRecord[]> => {
  const snapshot = await getDocs(collection(db, OUTLOOK_RECORDS_COLLECTION));
  const records = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as OutlookMeetingRecord));

  return records
    .filter(record => year === undefined || record.year === year)
    .sort((a, b) => {
      if (a.eventDateRange !== b.eventDateRange) {
        return a.eventDateRange.localeCompare(b.eventDateRange, 'zh-Hant');
      }
      return a.eventLocation.localeCompare(b.eventLocation, 'zh-Hant');
    });
};

export const saveOutlookRecord = async (record: OutlookMeetingRecord): Promise<void> => {
  const now = new Date().toISOString();
  const nextRecord: OutlookMeetingRecord = {
    ...record,
    updatedAt: now,
    createdAt: record.createdAt || now,
    submittedAt: record.status === 'review' ? record.submittedAt || now : record.submittedAt,
    publishedAt: record.status === 'published' ? record.publishedAt || now : record.publishedAt,
  };

  const { id, ...data } = nextRecord;
  const dataToSave = removeUndefined(data);
  dataToSave.statsSheetSource = deleteField();
  dataToSave.statsSync = deleteField();
  dataToSave.syncedStats = deleteField();
  await setDoc(doc(db, OUTLOOK_RECORDS_COLLECTION, id), dataToSave, { merge: true });
};

export const getOutlookRecordForRecorder = async (recordId: string): Promise<OutlookMeetingRecord> => {
  const { functions } = await import("../firebase");
  const getRecord = httpsCallable(functions, 'getOutlookRecordForRecorder');
  const result = await getRecord({ recordId });
  return (result.data as { record: OutlookMeetingRecord }).record;
};

export const saveOutlookRecordDraftForRecorder = async (
  recordId: string,
  record: Partial<OutlookMeetingRecord>
): Promise<OutlookMeetingRecord> => {
  const { functions } = await import("../firebase");
  const saveDraft = httpsCallable(functions, 'saveOutlookRecordDraftForRecorder');
  const result = await saveDraft({ recordId, record });
  return (result.data as { record: OutlookMeetingRecord }).record;
};

export const submitOutlookRecordForReview = async (
  recordId: string,
  record: Partial<OutlookMeetingRecord>
): Promise<OutlookMeetingRecord> => {
  const { functions } = await import("../firebase");
  const submitRecord = httpsCallable(functions, 'submitOutlookRecordForReview');
  const result = await submitRecord({ recordId, record });
  return (result.data as { record: OutlookMeetingRecord }).record;
};

// --- Admin & System Settings ---

export const getAllowedAdmins = async (): Promise<string[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, ACCESS_DOC_ID));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data?.adminEmails || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching allowed admins:", error);
    return [];
  }
};

export const updateAllowedAdmins = async (emails: string[]): Promise<void> => {
  await setDoc(doc(db, SETTINGS_COLLECTION, ACCESS_DOC_ID), {
    adminEmails: emails,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

export const getAllowedInventoryUsers = async (): Promise<string[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, INVENTORY_ACCESS_DOC_ID));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data?.inventoryEmails || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching inventory users:", error);
    return [];
  }
};

export const updateAllowedInventoryUsers = async (emails: string[]): Promise<void> => {
  await setDoc(doc(db, SETTINGS_COLLECTION, INVENTORY_ACCESS_DOC_ID), {
    inventoryEmails: emails,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

/**
 * 讀取各梯次的現場清點通行碼。Firestore 規則只放行管理員，
 * 其他人（含通行碼使用者本身）拿不到明碼。
 */
export const getInventoryAccessCodes = async (): Promise<InventoryAccessCodeMap> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, INVENTORY_CODES_DOC_ID));
    if (docSnap.exists()) {
      return (docSnap.data()?.codes || {}) as InventoryAccessCodeMap;
    }
    return {};
  } catch (error) {
    console.error("Error fetching inventory access codes:", error);
    return {};
  }
};

/** 建立或更新某一梯的通行碼；傳入 active: false 即可當場停用。 */
export const saveInventoryAccessCode = async (
  eventId: string,
  entry: InventoryAccessCode,
  actor: string
): Promise<void> => {
  await setDoc(doc(db, SETTINGS_COLLECTION, INVENTORY_CODES_DOC_ID), {
    codes: {
      [eventId]: {
        ...entry,
        updatedAt: new Date().toISOString(),
        updatedBy: actor,
      },
    },
    updatedAt: new Date().toISOString(),
  }, { merge: true });
};

/**
 * Get the current system active year (e.g., 2025, 2026)
 */
export const getSystemYear = async (): Promise<number> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, GENERAL_SETTINGS_DOC_ID));
    if (docSnap.exists()) {
      return docSnap.data()?.activeYear || new Date().getFullYear();
    }
    return new Date().getFullYear();
  } catch (error) {
    console.error("Error fetching system year:", error);
    return new Date().getFullYear();
  }
};

/**
 * Update the system active year
 */
export const updateSystemYear = async (year: number): Promise<void> => {
  await setDoc(doc(db, SETTINGS_COLLECTION, GENERAL_SETTINGS_DOC_ID), {
    activeYear: year,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

/**
 * Get the watchlist from Firestore
 * Returns array of WatchlistItem objects with name and church
 */
export const getWatchlist = async (): Promise<WatchlistItem[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'watchlist'));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data?.items || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching watchlist:", error);
    return [];
  }
};

/**
 * Update the entire watchlist
 */
export const updateWatchlist = async (items: WatchlistItem[]): Promise<void> => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'watchlist'), {
    items: items,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

/**
 * Clear all historical records from Firestore
 * WARNING: This operation cannot be undone
 *
 * Uses batched deletion to avoid Firestore limits:
 * - Max 500 operations per batch
 * - Processes all documents in chunks of 500
 */
export const clearAllHistoricalData = async (): Promise<number> => {
  const snapshot = await getDocs(collection(db, HIST_COLLECTION));
  const totalDocs = snapshot.size;

  if (totalDocs === 0) {
    return 0;
  }

  // ✅ Split documents into batches of 500 (Firestore batch limit)
  const BATCH_SIZE = 500;
  const batches: WriteBatch[] = [];
  let currentBatch = writeBatch(db);
  let operationCount = 0;

  snapshot.docs.forEach((docSnap) => {
    currentBatch.delete(docSnap.ref);
    operationCount++;

    // When batch is full, save it and start a new one
    if (operationCount === BATCH_SIZE) {
      batches.push(currentBatch);
      currentBatch = writeBatch(db);
      operationCount = 0;
    }
  });

  // Add the last batch if it has any operations
  if (operationCount > 0) {
    batches.push(currentBatch);
  }

  // ✅ Commit all batches sequentially
  console.log(`Deleting ${totalDocs} documents in ${batches.length} batch(es)...`);

  for (let i = 0; i < batches.length; i++) {
    await batches[i].commit();
    console.log(`Batch ${i + 1}/${batches.length} committed (${Math.min((i + 1) * BATCH_SIZE, totalDocs)}/${totalDocs} documents deleted)`);
  }

  return totalDocs;
};
